import { taxComplianceApiBaseUrl } from '../../shared/env';
import { apiErrorFromResponse } from '../../shared/apiErrorFromResponse';
import { createJsonClient } from '../../shared/httpClient';
import { refreshAccessToken } from '../../shared/tokenRefresh';
import type { TaxPaymentsGateway } from './TaxPaymentsGateway';
import type { TaxPaymentAction, TaxPaymentDetail } from './types';
import type {
  DashboardDetail, DashboardQuery, DashboardResponse, DownloadedDocument, InsufficientFundsInput, RecordOutsideInput, TaxPaymentScope, VoidOutsideInput,
} from './dashboardTypes';

const COMPANY = '/api/tax-payments';
const PLATFORM = '/api/platform/tax-payments';
const enc = encodeURIComponent;

/** Builds the dashboard query string. An empty value is left out; `companyId` is sent only on the platform door, which also asks for every company when none is chosen. */
export function dashboardQueryString(query: DashboardQuery, scope: TaxPaymentScope): string {
  const params = new URLSearchParams();
  const { companyId, ...rest } = query;
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  if (scope === 'platform') {
    if (companyId) params.set('companyId', companyId);
    else params.set('allCompanies', 'true');
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

function fileNameFrom(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try { return decodeURIComponent(star[1]); } catch { /* fall through to the plain name */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() || fallback;
}

export class HttpTaxPaymentsGateway implements TaxPaymentsGateway {
  private readonly request = createJsonClient(taxComplianceApiBaseUrl);

  get(id: string, signal?: AbortSignal): Promise<TaxPaymentDetail> {
    return this.request(`${COMPANY}/${enc(id)}`, undefined, signal);
  }

  act(id: string, action: TaxPaymentAction): Promise<TaxPaymentDetail> {
    return this.request(`${COMPANY}/${enc(id)}/${action}`, { method: 'POST' });
  }

  listDashboard(query: DashboardQuery, scope: TaxPaymentScope, signal?: AbortSignal): Promise<DashboardResponse> {
    return this.request(`${scope === 'platform' ? PLATFORM : COMPANY}/dashboard${dashboardQueryString(query, scope)}`, undefined, signal);
  }

  getDashboardDetail(id: string, scope: TaxPaymentScope, signal?: AbortSignal): Promise<DashboardDetail> {
    return this.request(scope === 'platform' ? `${PLATFORM}/${enc(id)}` : `${COMPANY}/${enc(id)}/dashboard`, undefined, signal);
  }

  async downloadDocument(paymentId: string, documentId: string, scope: TaxPaymentScope, signal?: AbortSignal): Promise<DownloadedDocument> {
    const path = `${scope === 'platform' ? PLATFORM : COMPANY}/${enc(paymentId)}/documents/${enc(documentId)}`;
    const doFetch = () => fetch(`${taxComplianceApiBaseUrl}${path}`, { method: 'GET', credentials: 'include', ...(signal ? { signal } : {}) });
    let response = await doFetch();
    if (response.status === 401 && (await refreshAccessToken())) response = await doFetch();
    if (!response.ok) throw await apiErrorFromResponse(response, `Request to the document download failed with ${response.status}`);
    return { blob: await response.blob(), fileName: fileNameFrom(response.headers.get('Content-Disposition'), 'payment-document') };
  }

  recordOutsidePayment(paymentId: string, input: RecordOutsideInput, idempotencyKey: string): Promise<DashboardDetail> {
    const form = new FormData();
    form.set('paymentDate', input.paymentDate);
    form.set('amount', String(input.amount));
    form.set('method', input.method);
    if (input.referenceNumber.trim()) form.set('referenceNumber', input.referenceNumber.trim());
    if (input.notes.trim()) form.set('notes', input.notes.trim());
    form.set('acknowledged', String(input.acknowledged));
    for (const file of input.files) form.append('files', file, file.name);
    return this.request(`${COMPANY}/${enc(paymentId)}/outside-record`, { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: form });
  }

  voidOutsidePayment(paymentId: string, input: VoidOutsideInput): Promise<DashboardDetail> {
    return this.request(`${COMPANY}/${enc(paymentId)}/outside-record/void`, { method: 'POST', body: JSON.stringify({ reason: input.reason, confirmed: input.confirmed }) });
  }

  recordInsufficientFunds(paymentId: string, input: InsufficientFundsInput): Promise<DashboardDetail> {
    return this.request(`${COMPANY}/${enc(paymentId)}/insufficient-funds`, {
      method: 'POST',
      body: JSON.stringify({
        dateIdentified: input.dateIdentified, reasonReference: input.reasonReference, internalNote: input.internalNote, acknowledged: input.acknowledged,
      }),
    });
  }

  addNote(paymentId: string, text: string): Promise<DashboardDetail> {
    return this.request(`${COMPANY}/${enc(paymentId)}/notes`, { method: 'POST', body: JSON.stringify({ text }) });
  }
}
